import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, post } from "@/lib/api";
import { FieldError } from "./States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { current: "", next: "", confirm: "" };
const FIELDS = [["current", "Current password", "current-password"], ["next", "New password", "new-password"], ["confirm", "Confirm new password", "new-password"]];

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [f, setF] = useState(blank);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF(blank);
      setErrors({});
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.current) errs.current = "Enter your current password";
    if (f.next.length < 8) errs.next = "Use at least 8 characters";
    else if (f.next === f.current) errs.next = "Choose a different password";
    if (f.next !== f.confirm) errs.confirm = "Passwords do not match";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password changed. Other devices have been signed out.");
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Change password</DialogTitle>
          <DialogDescription>You'll stay signed in here. Other devices will be signed out.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          {FIELDS.map(([k, label, ac]) => (
            <div key={k}>
              <Label htmlFor={`cp-${k}`}>{label}</Label>
              <Input id={`cp-${k}`} type="password" autoComplete={ac} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5 h-11" data-testid={`change-password-${k}-input`} />
              <FieldError message={errors[k]} testId={`change-password-${k}-error`} />
            </div>
          ))}
          {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="change-password-form-error">{errors.form}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="change-password-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="h-11 bg-blue-700 hover:bg-blue-800" data-testid="change-password-save-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Change password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
